import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { makeStyles, Button, IconButton, useMediaQuery } from "@material-ui/core";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ActionDialog from "../../common/ActionDialog";
import theme from "../../../themes";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../common/Loader/Loader";
import { ToastContainer } from "react-toastify";
import SuccessDialog from "../../common/SuccessDialog";
import { UserTypeContext } from "../../../UserTypeContext";
import { useLocation } from "react-router-dom";
import {
  BannerInput,
  BannerContentTypeEnum,
  CategoryAdvertisingBannerReturn,
  GetActiveCategoryAdvertisingBannersForChamberMemberDocument,
  GetUserQuery,
  useGetActiveCategoryAdvertisingBannersForChamberMemberQuery,
  UserType,
  useUpdateBannerContentForCategoryAdvertisingBannerMutation,
} from "../../../graphql/types";
import omitDeep from "omit-deep-lodash";
import { useGetCategoryIdToHumanReadableName } from "../../common/Hooks/useGetCategoryIdToHumanReadableName";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import BannerPickerAgreementOverlay from "../../common/BannerPickerAgreementOverlay";
import { useCmpBannerDisclaimer } from "./CmpProfile/CmpBannerDisclaimer";
import CmpDesignDialog, { CMP_DESIGN_GREEN, CMP_DESIGN_ICONS, CMP_DESIGN_RED } from "./CmpProfile/CmpDesignDialog";
import useHandleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement from "../../common/Hooks/useHandleAcceptanceOfChamberMemberCategoryAdvertisingBannerAgreement";
import ChamberMemberMemberProfileAndPerksBannersAgreementModal from "../../common/ChamberMemberMemberProfileAndPerksBannersAgreementModal";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf, openMemberInstructionPdf } from "../../common/openInstructionPdf";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ComingSoonDialog from "../../common/ComingSoonDialog";
import UploadRoundIcon from "../../../images/upload-rounded.png";
import EditOrangeIcon from "../../../images/edit-icon-orange.png";
import TrashIcon from "../../../images/trash.png";
import VideoIcon from "../../../images/lets-icons_video.png";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import BannerUploadModal from "../ChamberAdmin/BannerUploadModal";
import BannerPreviewModal from "../ChamberAdmin/BannerPreviewModal";
import DeleteBannerModal from "../ChamberAdmin/DeleteBannerModal";
import CategoryBannerPhoneMockup from "../../../images/cateogrybanner.jpeg";
import useCheckIfCategoryAdvertisingBannersActiveForChamberMember from "../../common/Hooks/useCheckIfCategoryAdvertisingBannersActiveForChamberMember.ts";
import useGetChamberMemberFromUserIdInAuth from "../../common/Hooks/useGetChamberMemberFromUserIdInAuth";
import useMemberDashboardAccess from "../../common/Hooks/useMemberDashboardAccess";
import "./cmpMemberPages.css";

/* CMP Accelerator design icons (Manage Category Advertising Banners). */
const CmpUploadIcon = ({ stroke, size }: { stroke: string; size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 15.5V4.5" />
    <path d="M6.8 8.7l5.2-5.2 5.2 5.2" />
    <path d="M4 15.5v2.7a2.3 2.3 0 002.3 2.3h11.4a2.3 2.3 0 002.3-2.3v-2.7" />
  </svg>
);
const CmpPencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="2" aria-hidden="true">
    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
  </svg>
);
const CmpTrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </svg>
);
const CmpPlayIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#E1A731" strokeWidth="1.6" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M10 8l6 4-6 4z" fill="none" strokeLinejoin="round" />
  </svg>
);

const useStyles = makeStyles((themes: any) => ({
  title: {
    marginLeft: "24px",
    marginRight: "24px",
    fontSize: "30px",
    color: "white",
    fontWeight: "bold",
    marginTop: "15px",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "12px",
      marginRight: "12px",
      fontSize: "22px",
    },
  },
  titleContent: {
    display: "flex",
    flexDirection: "column-reverse",
    backgroundColor: theme.palette.primary.main,
    overflowX: "hidden",
    [theme.breakpoints.up("lg")]: {
      paddingTop: "30px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "12px 0",
    },
  },
  pageHeaderButtonRow: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column" as const,
      alignItems: "stretch",
      gap: "8px",
      width: "100%",
      "& > div": { width: "100%", paddingLeft: "0 !important" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  content: {
    display: "flex",
    background: "#FFFFFF",
    minHeight: "80vh",
    paddingBottom: 30,
    borderRadius: "16px",
    marginTop: "33px",
    margin: "20px",
    flexDirection: "column",
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      margin: "0 8px",
      marginTop: 16,
    },
  },
  inactiveSection: {
    maxWidth: "1096px",
    width: "100%",
    margin: "50px auto",
    padding: "0px 30px 30px 30px",
    border: "1px solid #9E9E9E",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "36px",
    position: "relative" as const,
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      padding: "0px 12px 16px 12px",
      margin: "20px auto",
    },
  },
  sectionHeaderBlack: {
    backgroundColor: "#000000",
    borderRadius: "30px",
    padding: "17px 18px",
    display: "inline-block",
    alignSelf: "flex-start",
    position: "relative" as const,
    top: "-30px",
    marginBottom: "-30px",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 16px",
      top: "-20px",
      marginBottom: "-12px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontSize: "20px",
    fontWeight: 700,
    fontFamily: "DM Sans, sans-serif",
    lineHeight: "24px",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      lineHeight: "18px",
    },
  },
  inactiveDescText: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "center" as const,
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
  },
  clickHereLink: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    cursor: "pointer",
    fontWeight: 700,
  },
  bannersGrid: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "14px",
    width: "100%",
    overflowX: "auto",
  },
  bannersLabelsRow: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "12px",
  },
  bannersCardsRow: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap" as const,
    justifyContent: "center" as const,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column" as const,
    },
  },
  bannerSlotDisabled: {
    width: "158px",
    height: "108px",
    backgroundColor: "#9E9E9E",
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "22px 12px",
    gap: "10px",
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  bannerSlotDisabledIcon: {
    width: "36px",
    height: "36px",
    filter: "brightness(0) invert(1)",
  },
  bannerSlotDisabledText: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "24px",
    color: "#FFFFFF",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
  },
  bannerLabel: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#888888",
    textAlign: "center" as const,
    lineHeight: "24px",
    width: "158px",
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  previewBannersButton: {
    backgroundColor: "#888888",
    color: "#FFFFFF",
    borderRadius: "12px",
    padding: "15px 40px",
    fontSize: "20px",
    fontWeight: 700,
    fontFamily: "DM Sans, sans-serif",
    lineHeight: "150%",
    textTransform: "uppercase" as const,
    cursor: "not-allowed",
    "&:hover": { backgroundColor: "#888888" },
    "&.Mui-disabled": {
      backgroundColor: "#888888",
      color: "#FFFFFF",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      fontSize: "16px",
      padding: "12px 20px",
    },
  },
  inactiveUploadDesc: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "center" as const,
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
  },
  inactiveQualityDesc: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "center" as const,
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
  },
  previewImage: {
    maxWidth: "100%",
    width: "700px",
    height: "auto",
    objectFit: "contain" as const,
  },
  activeSection: {
    maxWidth: "1096px",
    width: "100%",
    margin: "30px auto",
    padding: "0px 30px 30px 30px",
    border: "1px solid #9E9E9E",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    position: "relative" as const,
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      padding: "0px 12px 16px 12px",
      margin: "16px auto",
    },
  },
  sectionHeaderGold: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "30px",
    padding: "17px 18px",
    display: "inline-block",
    alignSelf: "flex-start",
    position: "relative" as const,
    top: "-30px",
    marginBottom: "-16px",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 16px",
      top: "-20px",
      marginBottom: "-8px",
      maxWidth: "calc(100% - 8px)",
    },
  },
  activeDescText: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "left" as const,
    alignSelf: "stretch" as const,
    width: "100%",
    margin: 0,
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
  activeQualityDesc: {
    fontSize: "16px",
    lineHeight: "24px",
    color: theme.palette.text.primary,
    textAlign: "left" as const,
    alignSelf: "stretch" as const,
    width: "100%",
    margin: "10px 0 0 0",
    fontFamily: "DM Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "20px",
    },
  },
}));

type CustomCategoryAdvertisingBannerReturn = CategoryAdvertisingBannerReturn & {
  humanReadableCategoryName: string;
};

const UniqueBannerPicker = ({
  categoryAdvertisingBanner,
  chamberMemberData,
  cmpLayout = false,
  cmpCategoryCount = 1,
}: {
  categoryAdvertisingBanner: CustomCategoryAdvertisingBannerReturn;
  chamberMemberData:
    | GetUserQuery["getUser"]["chamberMemberInfo"]
    | null
    | undefined;
  /** CMP Accelerator design layout (CMP members only). */
  cmpLayout?: boolean;
  /** How many purchased categories the member has; the design pill names the category only when there are several. */
  cmpCategoryCount?: number;
}): ReactElement => {
  const [bannerUpdateInProgress, setBannerUpdateInProgress] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [activeBannerIndex, setActiveBannerIndex] = useState(1);
  const [bannerPreviewOpen, setBannerPreviewOpen] = useState(false);
  const [deleteBannerIndex, setDeleteBannerIndex] = useState<number | null>(null);

  const [updateBannerContentForCategoryAdvertisingBanner] =
    useUpdateBannerContentForCategoryAdvertisingBannerMutation();

  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");

  // Banner data: map existing banners to 4 slots
  const getBannersData = useCallback(() => {
    const existingBanner = categoryAdvertisingBanner?.bannerSettings?.activationInfo?.banner;
    const emptyBanner = { image: null as string | null, videoUrl: null as string | null, redirectUrl: null as string | null, waitingForApproval: false };
    if (existingBanner && existingBanner.length > 0) {
      const mapped = existingBanner.map((b: any) => ({
        image: b?.contentType === "IMAGE" ? b?.contentUrl || null : null,
        videoUrl: b?.contentType === "VIDEO" ? b?.contentUrl || null : null,
        redirectUrl: b?.imageLink || null,
        waitingForApproval: b?.waitingForApprovalByChamberAdmin ?? false,
      }));
      while (mapped.length < 4) mapped.push({ ...emptyBanner });
      return mapped;
    }
    return [{ ...emptyBanner }, { ...emptyBanner }, { ...emptyBanner }, { ...emptyBanner }];
  }, [categoryAdvertisingBanner?.bannerSettings?.activationInfo?.banner]);

  const [bannersData, setBannersData] = useState(getBannersData());

  useEffect(() => {
    setBannersData(getBannersData());
  }, [getBannersData]);

  const handleBannerUpdate = useCallback(
    async (
      updatedBanner: BannerInput[],
      isSlidePositioningUpdate?: boolean
    ) => {
      setBannerUpdateInProgress(true);

      await updateBannerContentForCategoryAdvertisingBanner({
        variables: {
          categoryId: categoryAdvertisingBanner?.categoryId || "",
          banner: omitDeep(updatedBanner, [
            "__typename",
            "waitingForApprovalByChamberAdmin",
          ]) as BannerInput[],
        },
        refetchQueries: [
          {
            query: GetActiveCategoryAdvertisingBannersForChamberMemberDocument,
          },
        ],
        awaitRefetchQueries: true,
      });

      setBannerUpdateInProgress(false);

      const isAutoApproveOrReposition =
        isSlidePositioningUpdate ||
        categoryAdvertisingBanner?.bannerSettings?.activationInfo
          ?.autoApproveBannerContentUpdate;

      const msg = isAutoApproveOrReposition
        ? enumStrings.chamberMember.categoryBannerUpdatedSuccessfully.replace(
            "{bannerNumber}",
            categoryAdvertisingBanner.humanReadableCategoryName
          )
        : enumStrings.chamberMember.categoryBannerSentForApproval.replace(
            "{bannerNumber}",
            categoryAdvertisingBanner.humanReadableCategoryName
          );
      setSuccessMessage(msg);
      setShowSuccessDialog(true);
    },
    [
      categoryAdvertisingBanner?.bannerSettings?.activationInfo
        ?.autoApproveBannerContentUpdate,
      categoryAdvertisingBanner?.categoryId,
      categoryAdvertisingBanner.humanReadableCategoryName,
      enumStrings.chamberMember.categoryBannerSentForApproval,
      enumStrings.chamberMember.categoryBannerUpdatedSuccessfully,
      updateBannerContentForCategoryAdvertisingBanner,
    ]
  );

  const handleOpenBannerModal = (index: number) => {
    setActiveBannerIndex(index);
    setBannerModalOpen(true);
  };

  const handleSaveBanner = useCallback((data: { image: string | null; videoUrl: string | null; redirectUrl: string | null; }) => {
    const newBannersData = [...bannersData];
    newBannersData[activeBannerIndex - 1] = {
      ...data,
      waitingForApproval: bannersData[activeBannerIndex - 1]?.waitingForApproval ?? false,
    };
    setBannersData(newBannersData);
    setBannerModalOpen(false);

    const bannerInputs: BannerInput[] = newBannersData
      .filter(b => b.image || b.videoUrl)
      .map((b, i) => ({
        contentUrl: b.image || b.videoUrl || "",
        imageLink: b.redirectUrl || undefined,
        contentType: b.videoUrl ? BannerContentTypeEnum.Video : BannerContentTypeEnum.Image,
        position: i,
      }));
    handleBannerUpdate(bannerInputs);
  }, [bannersData, activeBannerIndex, handleBannerUpdate]);

  const handleMoveBanner = (index: number, direction: 'left' | 'right') => {
    const newBannersData = [...bannersData];
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newBannersData.length) {
      const temp = newBannersData[index];
      newBannersData[index] = newBannersData[targetIndex];
      newBannersData[targetIndex] = temp;
      setBannersData(newBannersData);

      const bannerInputs: BannerInput[] = newBannersData
        .filter(b => b.image || b.videoUrl)
        .map((b, i) => ({
          contentUrl: b.image || b.videoUrl || "",
          imageLink: b.redirectUrl || undefined,
          contentType: b.videoUrl ? BannerContentTypeEnum.Video : BannerContentTypeEnum.Image,
          position: i,
        }));
      handleBannerUpdate(bannerInputs, true);
    }
  };

  const handleDeleteBanner = (index: number) => {
    const newBannersData = [...bannersData];
    newBannersData[index] = { image: null, videoUrl: null, redirectUrl: null, waitingForApproval: false };
    setBannersData(newBannersData);

    const bannerInputs: BannerInput[] = newBannersData
      .filter(b => b.image || b.videoUrl)
      .map((b, i) => ({
        contentUrl: b.image || b.videoUrl || "",
        imageLink: b.redirectUrl || undefined,
        contentType: b.videoUrl ? BannerContentTypeEnum.Video : BannerContentTypeEnum.Image,
        position: i,
      }));
    handleBannerUpdate(bannerInputs);
  };

  const activeBanners = bannersData.filter(b => b.image || b.videoUrl);

  const chamberInfo = useGetChamberFromUserIdInAuth();

  const {
    chamberMembeCategoryAdvertisingBannerAgreementIsAccepted,
    handleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement,
  } = useHandleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement({
    categoryId: categoryAdvertisingBanner.categoryId,
    chamberMemberHasAcceptedAgreement:
      categoryAdvertisingBanner?.bannerSettings?.activationInfo
        ?.chamberMemberHasAcceptedAgreement,
  });

  const [
    isCategoryAdvertisingBannerAgreementModalOpen,
    setIsCategoryAdvertisingBannerAgreementModalOpen,
  ] = useState(false);

  const openChamberMemberCategoryAdvertisingBannerAgreementModal =
    useCallback(() => {
      setIsCategoryAdvertisingBannerAgreementModalOpen(true);
    }, []);

  // MD-B1 / MD-B3: CMP members see the design disclaimer on the first upload click,
  // then the upload opens for that slot (no overlay over the tiles).
  const categoryDisclaimer = useCmpBannerDisclaimer({
    kind: "category",
    enabled: cmpLayout,
    categoryId: categoryAdvertisingBanner.categoryId,
    acceptedVersion:
      categoryAdvertisingBanner?.bannerSettings?.activationInfo
        ?.chamberMemberAcceptedAgreementVersion,
  });
  const openCmpUpload = (bannerNumber: number) =>
    categoryDisclaimer.guard(() => handleOpenBannerModal(bannerNumber));

  const modals = (
    <>
      {cmpLayout ? categoryDisclaimer.dialog : (
      <ChamberMemberMemberProfileAndPerksBannersAgreementModal
        open={isCategoryAdvertisingBannerAgreementModalOpen}
        handleClose={() =>
          setIsCategoryAdvertisingBannerAgreementModalOpen(false)
        }
        handleAcceptanceOfChamberMemberMemberProfileAndPerksBannersAgreement={
          handleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement
        }
        chamberName={chamberInfo?.name || ""}
        isCategoryBanner
      />
      )}
      <BannerPreviewModal
        open={bannerPreviewOpen}
        onClose={() => setBannerPreviewOpen(false)}
        banners={activeBanners}
        phoneMockupImage={CategoryBannerPhoneMockup}
      />
      <BannerUploadModal
        open={bannerModalOpen}
        onClose={() => setBannerModalOpen(false)}
        bannerIndex={activeBannerIndex}
        memberDesign={cmpLayout}
        initialData={bannersData[activeBannerIndex - 1] || { image: null, videoUrl: null, redirectUrl: null }}
        onSave={handleSaveBanner}
      />
      {cmpLayout ? (
        // CMP design (ACC:1322-1333); this page deletes at once, so no "Click Update" line (MD-U3).
        <CmpDesignDialog
          open={deleteBannerIndex !== null}
          onClose={() => setDeleteBannerIndex(null)}
          testId="cmp-delete-banner-dialog" icon={CMP_DESIGN_ICONS.trash} iconBg={CMP_DESIGN_RED}
          title="Delete Banner" actionWidth={200}
          lines={["Are you sure you want to delete this banner?"]}
          actions={[{ label: "Delete", kind: "danger", testId: "cmp-delete-banner-confirm", onClick: () => {
            if (deleteBannerIndex !== null) { handleDeleteBanner(deleteBannerIndex); setDeleteBannerIndex(null); }
          } }]}
        />
      ) : (
      <DeleteBannerModal
        open={deleteBannerIndex !== null}
        onClose={() => setDeleteBannerIndex(null)}
        onDelete={() => {
          if (deleteBannerIndex !== null) {
            handleDeleteBanner(deleteBannerIndex);
            setDeleteBannerIndex(null);
          }
        }}
      />
      )}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        autoDismissMs={15000}
      />
    </>
  );

  if (cmpLayout) {
    // CMP Accelerator design: purchased "Category Advertising Banners" section, 4 tiles in one row.
    const lastIndex = bannersData.length - 1;
    return (
      <section className="cmp-mp-section cmp-mp-section-on" data-testid="cmp-banners-purchased">
        <h2 className="cmp-mp-pill cmp-mp-pill-on">
          {cmpCategoryCount > 1
            ? enumStrings.chamberMember.categoryAdvertisingActiveBannerTitle.replace(
                "{categoryName}",
                categoryAdvertisingBanner.humanReadableCategoryName
              )
            : "Category Advertising Banners"}
        </h2>
        <p className="cmp-mp-upload-note">
          {enumStrings.chamberMember.categoryAdvertisingBannerUploadDesc}
          <br />
          For best quality, we recommend an aspect ratio of <b>16:9 with a minimum size of 1280 × 720.</b>
        </p>
        {bannerUpdateInProgress && (
          <div className="cmp-mp-busy">
            <Loader size={80} fullPage={false} />
          </div>
        )}
        <div className="cmp-mp-tiles" data-testid="cmp-banner-tiles">
          {bannersData.map((banner, index) => {
            const hasBanner = !!(banner.image || banner.videoUrl);
            const isApproved = hasBanner && !banner.waitingForApproval;
            const label = `Banner ${index + 1}`;
            // MD-B4 (ACC:3079-3084): an arrow is gold only when the next slot has a banner.
            const slotHasBanner = (slot: number) => !!(bannersData[slot]?.image || bannersData[slot]?.videoUrl);
            const canMoveLeft = index > 0 && slotHasBanner(index - 1);
            const canMoveRight = index < lastIndex && slotHasBanner(index + 1);
            return (
              <div className="cmp-mp-tile" key={index}>
                <div className="cmp-mp-tile-label cmp-mp-tile-label-on">{label}</div>
                {hasBanner ? (
                  <div className="cmp-mp-slot-filled">
                    {banner.image ? (
                      <div className="cmp-mp-slot-media">
                        <img src={banner.image} alt={label} />
                      </div>
                    ) : (
                      <div className="cmp-mp-slot-video">
                        <CmpPlayIcon />
                        <span>{enumStrings.editMemberForm?.watchVideo || "Watch Video"}</span>
                      </div>
                    )}
                    <div className="cmp-mp-slot-tools">
                      <div>
                        <button type="button" className="cmp-mp-tool-edit" aria-label={`Edit ${label}`} onClick={() => openCmpUpload(index + 1)}>
                          <CmpPencilIcon />
                        </button>
                        <button type="button" className="cmp-mp-tool-delete" aria-label={`Delete ${label}`} onClick={() => setDeleteBannerIndex(index)}>
                          <CmpTrashIcon />
                        </button>
                      </div>
                      <div>
                        <button type="button" className="cmp-mp-tool-move" aria-label={`Move ${label} left`} disabled={!canMoveLeft} onClick={() => handleMoveBanner(index, "left")}>
                          ❮
                        </button>
                        <button type="button" className="cmp-mp-tool-move" aria-label={`Move ${label} right`} disabled={!canMoveRight} onClick={() => handleMoveBanner(index, "right")}>
                          ❯
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="cmp-mp-slot-empty"
                    role="button"
                    tabIndex={0}
                    onClick={() => openCmpUpload(index + 1)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openCmpUpload(index + 1);
                      }
                    }}
                  >
                    <CmpUploadIcon stroke="#E1A731" size={24} />
                    <span>{enumStrings.manageChamberEngage.categoryAdvertisingBanners.uploadBanner}</span>
                  </div>
                )}
                <div
                  className={`cmp-mp-tile-status ${
                    !hasBanner
                      ? "cmp-mp-tile-status-none"
                      : isApproved
                      ? "cmp-mp-tile-status-approved"
                      : "cmp-mp-tile-status-waiting"
                  }`}
                >
                  {!hasBanner ? "–" : isApproved ? enumStrings.approved : enumStrings.waitingForApproval}
                </div>
              </div>
            );
          })}
        </div>
        <div className="cmp-mp-preview-row cmp-mp-preview-row-on">
          <button
            type="button"
            className="cmp-mp-preview-on"
            onClick={() => setBannerPreviewOpen(true)}
          >
            Preview Banners
          </button>
        </div>
        {modals}
      </section>
    );
  }

  return (
    <div className={classes.activeSection}>
      <div className={classes.sectionHeaderGold}>
        <p className={classes.sectionTitle}>
          {enumStrings.chamberMember.categoryAdvertisingActiveBannerTitle.replace(
            "{categoryName}",
            categoryAdvertisingBanner.humanReadableCategoryName
          )}
        </p>
      </div>

      <p className={classes.activeDescText}>
        {enumStrings.chamberMember.categoryAdvertisingActiveApprovalDesc.replace(
          "{chamberName}",
          chamberInfo?.name || ""
        )}
      </p>

      <p className={classes.activeDescText} style={{ marginTop: "10px" }}>
        {enumStrings.chamberMember.categoryAdvertisingBannerUploadDesc}
      </p>
      <p className={classes.activeQualityDesc} dangerouslySetInnerHTML={{
        __html: enumStrings.chamberMember.categoryAdvertisingBannerQualityDesc
      }} />

      <div style={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <ChamberMemberMemberProfileAndPerksBannersAgreementModal
          open={isCategoryAdvertisingBannerAgreementModalOpen}
          handleClose={() =>
            setIsCategoryAdvertisingBannerAgreementModalOpen(false)
          }
          handleAcceptanceOfChamberMemberMemberProfileAndPerksBannersAgreement={
            handleAcceptanceOfChamberMembeCategoryAdvertisingBannerAgreement
          }
          chamberName={chamberInfo?.name || ""}
          isCategoryBanner
        />
        <BannerPickerAgreementOverlay
          chamberMemberMemberProfileAndPerksBannersAgreementIsAccepted={
            chamberMembeCategoryAdvertisingBannerAgreementIsAccepted
          }
          openChamberMemberMemberProfileAndPerksBannersAgreementModal={
            openChamberMemberCategoryAdvertisingBannerAgreementModal
          }
          paddingLeftPx={30}
          userType={UserType.ChamberMemberAdmin}
        />

        {bannerUpdateInProgress && (
          <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
            <Loader size={80} fullPage={false} />
          </div>
        )}

        {/* Banner Upload Grid - All 4 in one row (2-col grid on mobile) */}
        <div
          style={isMobile ? {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
            marginTop: "16px",
            marginBottom: "14px",
            width: "100%",
          } : {
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: "20px",
            marginTop: "20px",
            justifyContent: "center",
            marginBottom: "14px",
          }}
        >
          {bannersData.map((banner, index) => {
            const hasBanner = banner.image || banner.videoUrl;
            const isApproved = hasBanner ? !banner.waitingForApproval : false;
            return (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: isMobile ? "8px" : "12px",
                width: isMobile ? "100%" : "auto",
                minWidth: 0,
              }}
            >
              <p
                style={{
                  textAlign: "center",
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  fontSize: isMobile ? "15px" : "20px",
                  lineHeight: isMobile ? "20px" : "24px",
                  margin: 0,
                }}
              >
                {enumStrings.manageChamberEngage.categoryAdvertisingBanners.bannerLabel.replace("{{number}}", String(index + 1))}
              </p>
              {banner.image || banner.videoUrl ? (
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    width: isMobile ? "100%" : "209px",
                    height: isMobile ? "150px" : "175px",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      position: "relative",
                      padding: "12px",
                      flex: 1,
                    }}
                  >
                    {banner.image ? (
                      <img
                        src={banner.image}
                        alt={`Banner ${index + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          fontSize: "20px",
                          fontWeight: 600,
                          gap: "8px",
                          border: `1px solid ${theme.palette.primary.main}`,
                          borderRadius: "4px",
                          boxSizing: "border-box",
                        }}
                      >
                        <img src={VideoIcon} alt="Video" style={{ width: "49px", height: "49px" }} />
                        {enumStrings.editMemberForm?.watchVideo || "Watch Video"}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 12px",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenBannerModal(index + 1)}
                        style={{
                          backgroundColor: "#E3E3E3",
                          borderRadius: "5px",
                          width: "36px",
                          height: "33px",
                          padding: "4px",
                        }}
                      >
                        <img src={EditOrangeIcon} alt="Edit" style={{ width: "20px", height: "20px", filter: "brightness(0)" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteBannerIndex(index)}
                        style={{
                          backgroundColor: (theme.palette as any).delete.main,
                          borderRadius: "5px",
                          width: "36px",
                          height: "33px",
                          padding: "4px",
                        }}
                      >
                        <img src={TrashIcon} alt="Delete" style={{ width: "20px", height: "22px", filter: "brightness(0) invert(1)" }} />
                      </IconButton>
                    </div>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveBanner(index, 'left')}
                        disabled={index === 0}
                        style={{
                          backgroundColor: index === 0 ? "#D9D9D9" : theme.palette.primary.main,
                          color: index === 0 ? "#666" : "white",
                          borderRadius: "5px",
                          minWidth: "29px",
                          width: "29px",
                          height: "26px",
                          padding: "4px",
                        }}
                      >
                        <ArrowBackIosIcon style={{ fontSize: "12px", marginLeft: "4px" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveBanner(index, 'right')}
                        disabled={index === bannersData.length - 1}
                        style={{
                          backgroundColor: index === bannersData.length - 1 ? "#D9D9D9" : theme.palette.primary.main,
                          color: index === bannersData.length - 1 ? "#666" : "white",
                          borderRadius: "5px",
                          minWidth: "29px",
                          width: "29px",
                          height: "26px",
                          padding: "4px",
                        }}
                      >
                        <ArrowForwardIosIcon style={{ fontSize: "12px" }} />
                      </IconButton>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => handleOpenBannerModal(index + 1)}
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    width: isMobile ? "100%" : "182px",
                    minWidth: isMobile ? 0 : "120px",
                    height: isMobile ? "90px" : "114px",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    padding: isMobile ? "12px 8px" : "22px 12px",
                    gap: isMobile ? "6px" : "10px",
                  }}
                >
                  <img
                    src={UploadRoundIcon}
                    alt="Upload"
                    style={{ width: isMobile ? "28px" : "36px", height: isMobile ? "28px" : "36px" }}
                  />
                  <p
                    style={{
                      color: theme.palette.primary.main,
                      fontSize: isMobile ? "12px" : "20px",
                      fontWeight: 600,
                      margin: 0,
                      lineHeight: isMobile ? "1.3" : "24px",
                      textAlign: "center",
                      whiteSpace: isMobile ? "normal" : "nowrap",
                    }}
                  >
                    {enumStrings.manageChamberEngage.categoryAdvertisingBanners.uploadBanner}
                  </p>
                </div>
              )}
              {hasBanner && (
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "24px",
                    fontStyle: "italic",
                    color: isApproved ? theme.palette.success.main : theme.palette.primary.main,
                    marginTop: "4px",
                  }}
                >
                  {isApproved ? enumStrings.approved : enumStrings.waitingForApproval}
                </span>
              )}
            </div>
            );
          })}
        </div>

        {/* Preview Banners Button */}
        {activeBanners.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "10px", marginBottom: "20px" }}>
            <Button
              variant="contained"
              onClick={() => setBannerPreviewOpen(true)}
              style={{
                backgroundColor: "#333",
                color: "white",
                fontSize: "16px",
                fontWeight: "bold",
                borderRadius: "25px",
                padding: "12px 40px",
                textTransform: "none",
              }}
            >
              {enumStrings.createMemberForm.previewBannersButton}
            </Button>
          </div>
        )}

        <BannerPreviewModal
          open={bannerPreviewOpen}
          onClose={() => setBannerPreviewOpen(false)}
          banners={activeBanners}
          phoneMockupImage={CategoryBannerPhoneMockup}
        />

        <BannerUploadModal
          open={bannerModalOpen}
          onClose={() => setBannerModalOpen(false)}
          bannerIndex={activeBannerIndex}
          memberDesign={cmpLayout}
          initialData={bannersData[activeBannerIndex - 1] || { image: null, videoUrl: null, redirectUrl: null }}
          onSave={handleSaveBanner}
        />

        {cmpLayout ? (
          // CMP design (ACC:1322-1333); this page deletes at once, so no "Click Update" line (MD-U3).
          <CmpDesignDialog
            open={deleteBannerIndex !== null}
            onClose={() => setDeleteBannerIndex(null)}
            testId="cmp-delete-banner-dialog" icon={CMP_DESIGN_ICONS.trash} iconBg={CMP_DESIGN_RED}
            title="Delete Banner" actionWidth={200}
            lines={["Are you sure you want to delete this banner?"]}
            actions={[{ label: "Delete", kind: "danger", testId: "cmp-delete-banner-confirm", onClick: () => {
              if (deleteBannerIndex !== null) { handleDeleteBanner(deleteBannerIndex); setDeleteBannerIndex(null); }
            } }]}
          />
        ) : (
        <DeleteBannerModal
          open={deleteBannerIndex !== null}
          onClose={() => setDeleteBannerIndex(null)}
          onDelete={() => {
            if (deleteBannerIndex !== null) {
              handleDeleteBanner(deleteBannerIndex);
              setDeleteBannerIndex(null);
            }
          }}
        />
        )}
      </div>
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        autoDismissMs={15000}
      />
    </div>
  );
};

const ManageAllCategoryAdvertisingBannersForChammberCategoryAdvertisingBannersChamberMember =
  (): ReactElement => {
    const classes = useStyles();
    const enumStrings = useEnumStrings();

    const {
      state: { userType },
    } = useContext(UserTypeContext);

    const location = useLocation();

    const rootAdminObj = // @ts-ignore
      enumStrings?.groups[userType][
        RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember
      ];

    const [isLoading, setIsLoading] = useState(true);
    const [contactChamberOpen, setContactChamberOpen] = useState(false);
    const [showComingSoon, setShowComingSoon] = useState(false);

    const { data: getActiveCategoryAdvertisingBannersForChamberMember } =
      useGetActiveCategoryAdvertisingBannersForChamberMemberQuery();

    const [enabledCatBanner, setEnabledCatBanner] = useState<
      CustomCategoryAdvertisingBannerReturn[]
    >([]);

    const { categoryIdToHumanReadableName } =
      useGetCategoryIdToHumanReadableName();

    const chamberMemberData = useGetChamberMemberFromUserIdInAuth();

    useEffect(() => {
      if (
        !getActiveCategoryAdvertisingBannersForChamberMember ||
        !categoryIdToHumanReadableName
      ) {
        return;
      }

      const res = [];
      for (const catBanner of getActiveCategoryAdvertisingBannersForChamberMember?.getActiveCategoryAdvertisingBannersForChamberMember ||
        []) {
        res.push({
          ...catBanner,
          humanReadableCategoryName:
            categoryIdToHumanReadableName[catBanner.categoryId],
        });
      }

      setEnabledCatBanner(res as CustomCategoryAdvertisingBannerReturn[]);

      setIsLoading(false);
    }, [
      categoryIdToHumanReadableName,
      getActiveCategoryAdvertisingBannersForChamberMember,
    ]);

    const chamberInfo = useGetChamberFromUserIdInAuth();

    const isCategoryAdvertisingBannersActiveForChamberMember =
      useCheckIfCategoryAdvertisingBannersActiveForChamberMember({
        userType,
      });

    // CMP Accelerator design applies only to CMP members; CPA-only members and staff keep the legacy page.
    const {
      access: memberAccess,
      loading: memberAccessLoading,
      error: memberAccessError,
    } = useMemberDashboardAccess();
    const cmpLayout = !!memberAccess?.cmpEnabled;
    // Wait for the member's access before choosing a layout so CMP members never flash the legacy header.
    const memberAccessPending =
      userType === UserType.ChamberMemberAdmin &&
      !memberAccess &&
      !memberAccessError &&
      memberAccessLoading;

    const sendCategoryRequest = () => {
          const memberName = chamberMemberData?.name || "";
          const chamberName = chamberInfo?.name || "";
          const toEmail = chamberInfo?.supportContactEmail || "";
          const subject = encodeURIComponent(
              enumStrings.createMemberForm.contactChamberEmailSubject.replace("{memberName}", memberName)
          );
          const body = encodeURIComponent(
              enumStrings.createMemberForm.contactChamberEmailBody
                  .replace("{chamberName}", chamberName)
                  .replace("{memberName}", memberName)
          );
          window.open(`mailto:${toEmail}?subject=${subject}&body=${body}`, "_blank");
          setContactChamberOpen(false);
    };
    const contactChamberDialog = cmpLayout ? (
      // MD-B6 (ACC:1679-1687): design pop-up frame, 16px/600 title, 14px body, full-width button.
      <CmpDesignDialog open={contactChamberOpen} onClose={() => setContactChamberOpen(false)}
        testId="cmp-purchase-category-dialog" icon={CMP_DESIGN_ICONS.mail} iconBg={CMP_DESIGN_GREEN}
        title={enumStrings.chamberMember.purchaseCategoryBannerTitle}
        lines={[enumStrings.chamberMember.purchaseCategoryBannerDesc]}
        actionWidth="100%"
        actions={[{ label: enumStrings.chamberMember.purchaseCategoryBannerSendRequest, kind: "gold", onClick: sendCategoryRequest }]} />
    ) : (
      <ActionDialog
        open={contactChamberOpen}
        onClose={() => setContactChamberOpen(false)}
        icon={<MailOutlineIcon style={{ color: "#FFFFFF", fontSize: "40px" }} />}
        iconBgColor="#4CAF50"
        title={enumStrings.chamberMember.purchaseCategoryBannerTitle}
        description={enumStrings.chamberMember.purchaseCategoryBannerDesc}
        buttonText={enumStrings.chamberMember.purchaseCategoryBannerSendRequest}
        onButtonClick={sendCategoryRequest}
      />
    );

    if (memberAccessPending) {
      return <Loader size={80} fullPage={false} />;
    }

    if (cmpLayout) {
      // CMP Accelerator design: "Manage Category Advertising Banners".
      const notPurchasedParts =
        enumStrings.chamberMember.categoryAdvertisingNotActivatedDesc.split("{clickHere}");
      return (
        <div className="cmp-mp-page" data-testid="cmp-banners-page">
          <header className="cmp-mp-heading">
            <h1>{rootAdminObj.title}</h1>
          </header>
          <div className="cmp-mp-card cmp-mp-card-banners">
            {isLoading ? (
              <Loader size={80} fullPage={false} />
            ) : isCategoryAdvertisingBannersActiveForChamberMember ? (
              enabledCatBanner.map((catBanner) => (
                <UniqueBannerPicker
                  key={catBanner.categoryId}
                  categoryAdvertisingBanner={catBanner}
                  chamberMemberData={chamberMemberData}
                  cmpLayout
                  cmpCategoryCount={enabledCatBanner.length}
                />
              ))
            ) : (
              <section className="cmp-mp-section cmp-mp-section-off" data-testid="cmp-banners-not-purchased">
                <h2 className="cmp-mp-pill cmp-mp-pill-off">
                  {enumStrings.chamberMember.categoryAdvertisingNotActivatedTitle}
                </h2>
                <p className="cmp-mp-text">{enumStrings.chamberMember.categoryAdvertisingIntro}</p>
                <p className="cmp-mp-text cmp-mp-text-last">
                  <span dangerouslySetInnerHTML={{ __html: notPurchasedParts[0] }} />
                  <span
                    className="cmp-mp-link"
                    role="button"
                    tabIndex={0}
                    onClick={() => setContactChamberOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setContactChamberOpen(true);
                      }
                    }}
                  >
                    {enumStrings.createMemberForm.clickHereLink}
                  </span>
                  {notPurchasedParts[1]}
                </p>
                <div className="cmp-mp-tiles cmp-mp-tiles-off" data-testid="cmp-banner-tiles">
                  {[1, 2, 3, 4].map((slot) => (
                    <div className="cmp-mp-tile" key={slot} aria-disabled="true">
                      <div className="cmp-mp-tile-label cmp-mp-tile-label-off">Banner {slot}</div>
                      <div className="cmp-mp-slot-off">
                        <CmpUploadIcon stroke="#fff" size={22} />
                        <span>{enumStrings.manageChamberEngage.categoryAdvertisingBanners.uploadBanner}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="cmp-mp-preview-row cmp-mp-preview-row-off">
                  <button type="button" className="cmp-mp-preview-off" aria-disabled="true">
                    Preview Banners
                  </button>
                </div>
              </section>
            )}
          </div>
          {contactChamberDialog}
        </div>
      );
    }

    return (
      <div style={{ overflowX: "hidden" }}>
        <div className={classes.titleContent}>
          <div
            className={classes.title}
            style={{
              flexDirection: "column",
            }}
          >
            <span>
              {rootAdminObj.title}
              {(location?.state as any)?.chamberName &&
                ` for ${(location?.state as any)?.chamberName}`}
            </span>
            <span
              style={{
                marginLeft: "1px",
                fontWeight: "normal",
                fontSize: "16px",
                lineHeight: "1.55",
              }}
            >
              {rootAdminObj.description} {enumStrings.watchVideoToLearnHow}
            </span>
            <div style={{ height: 10 }} />
            <div
              className={classes.pageHeaderButtonRow}
              style={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: "row",
              }}
            >
              <div style={{ paddingBottom: "10px" }}>
                <VideoTutorialButton
                  tutorialUrl="https://share.synthesia.io/embeds/videos/fe255c22-da44-440d-b8d4-c175bc287a23"
                />
              </div>
              <div style={{ paddingBottom: "10px", paddingLeft: "10px" }}>
                <ExportButton
                  customOnClick={() =>
                    userType === UserType.ChamberMemberAdmin
                      ? openMemberInstructionPdf(
                          isCategoryAdvertisingBannersActiveForChamberMember
                            ? "Member Dashboard - Category Banners  ACTIVE.pdf"
                            : "Member Dashboard - Category Banners  INACTIVE.pdf"
                        )
                      : openInstructionPdf("Activate Category Member Advertising Banner.pdf")
                  }
                  disablePadding
                  icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
                  label={enumStrings.manageChamberEngage.viewInstructions}
                />
              </div>
            </div>
          </div>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
          />
        </div>
        <div className={classes.content}>
          {isLoading ? (
            <Loader size={80} fullPage={false} />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                marginTop: 40,
              }}
            >
              {isCategoryAdvertisingBannersActiveForChamberMember ? (
                enabledCatBanner.map((catBanner) => (
                  <UniqueBannerPicker
                    key={catBanner.categoryId}
                    categoryAdvertisingBanner={catBanner}
                    chamberMemberData={chamberMemberData}
                  />
                ))
              ) : (
                <div className={classes.inactiveSection}>
                  <div className={classes.sectionHeaderBlack}>
                    <p className={classes.sectionTitle}>
                      {enumStrings.chamberMember.categoryAdvertisingNotActivatedTitle}
                    </p>
                  </div>

                  <p className={classes.inactiveDescText}>{enumStrings.chamberMember.categoryAdvertisingIntro}</p>

                  <p className={classes.inactiveDescText}>
                    <span dangerouslySetInnerHTML={{
                      __html: enumStrings.chamberMember.categoryAdvertisingNotActivatedDesc
                        .split("{clickHere}")[0]
                    }} />
                    <span
                      className={classes.clickHereLink}
                      onClick={() => setContactChamberOpen(true)}
                    >
                      {enumStrings.createMemberForm.clickHereLink}
                    </span>
                    {enumStrings.chamberMember.categoryAdvertisingNotActivatedDesc
                      .split("{clickHere}")[1]}
                  </p>

                  {/* Design: four disabled banner slots and Preview Banners while not purchased. */}
                  <div data-testid="inactive-banner-slots" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16, margin: "8px 0 16px", opacity: 0.55 }}>
                    {[1, 2, 3, 4].map((slot) => (
                      <div key={slot} aria-disabled="true" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>Banner {slot}</div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, aspectRatio: "16 / 9",
                          borderRadius: 10, background: "#9E9E9E", color: "#FFFFFF", fontSize: 13, fontWeight: 600, cursor: "not-allowed" }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 15.5V4.5" /><path d="M6.8 8.7l5.2-5.2 5.2 5.2" /><path d="M4 15.5v2.7a2.3 2.3 0 002.3 2.3h11.4a2.3 2.3 0 002.3-2.3v-2.7" /></svg>
                          <span>Upload Banner</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <button type="button" disabled style={{ background: "#CFCFCF", color: "#FFFFFF", border: "none", borderRadius: 8, padding: "11px 22px", fontSize: 14, fontWeight: 600, cursor: "not-allowed" }}>Preview Banners</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <ComingSoonDialog
          open={showComingSoon}
          onClose={() => setShowComingSoon(false)}
        />

        {/* Contact Chamber Dialog */}
        {contactChamberDialog}
      </div>
    );
  };

export default ManageAllCategoryAdvertisingBannersForChammberCategoryAdvertisingBannersChamberMember;
